import { get, post } from "./client";

export type BrokenOrg = {
  id: string;
  name: string;
  companyId: string | null;
  status: string;
  classification: "unlinked" | "dangling" | "no-firm-ref";
  memberCount: number;
  entitlementCount: number;
  possibleDuplicate?: { organisationId: string; organisationName: string };
};

export type OrphanGroup = {
  firmRef: string | null;
  count: number;
  class: "legacy" | "blank" | "placeholder";
};

export type OrphanSubmissionsResponse = {
  orphans: OrphanGroup[];
  requestId: string | null;
};

export type RepointOutcome = {
  outcome: string;
  organisation?: unknown;
  memberCount?: number;
  entitlementCount?: number;
};

export type SafeRepointParams = {
  orgId: string;
  targetCompanyId: string;
  expectedCurrentCompanyId?: string | null;
};

export type ReassignParams = {
  userId: string;
  firmRef: string;
  firmName: string;
  fromFirmRefs: string[];
};

export type ReassignResponse = {
  message: string;
  updatedSubmissions: number;
  updatedDocumentFiles: number;
  updatedRefereeFiles: number;
  skippedSubmissions?: number;
};

export type ReassignPrecheckResponse = {
  userId: string;
  submissionsByFirmRef: { firmRef: string; firmName: string; count: number }[];
  memberships: { firmRef: string; firmName: string }[];
  multiFirm: boolean;
};

export type CompanyHeldResult = {
  companyId: string;
  held: boolean;
  organisation?: { id: string; name: string; status: string };
};

async function getDanglingOrgs(): Promise<{
  data: BrokenOrg[];
  total: number;
  truncated: boolean;
}> {
  return get("/v1/developer/remediation/dangling-orgs");
}

async function getOrphanSubmissions(): Promise<OrphanSubmissionsResponse> {
  return get("/v1/developer/remediation/orphan-submissions");
}

async function getCompaniesHeld(companyIds: string[]): Promise<{ data: CompanyHeldResult[] }> {
  const query = new URLSearchParams({ companyIds: companyIds.join(",") });
  return get(`/v1/developer/remediation/companies-held?${query.toString()}`);
}

async function safeRepoint(params: SafeRepointParams): Promise<{ data: RepointOutcome }> {
  return post("/v1/developer/remediation/safe-repoint", params);
}

async function reassign(params: ReassignParams): Promise<ReassignResponse> {
  return post("/v1/developer/remediation/reassign", params);
}

/**
 * `targetId` must be the id produced by `getPrecheckTargetId` (remediation.ts),
 * never a raw route/accounts id passed through unchecked.
 */
async function getReassignPrecheck(targetId: string): Promise<ReassignPrecheckResponse> {
  return get(`/v1/developer/users/${targetId}/reassign-precheck`);
}

export const accountRemediationService = {
  getDanglingOrgs,
  getOrphanSubmissions,
  getCompaniesHeld,
  safeRepoint,
  reassign,
  getReassignPrecheck,
};
